import { useTranslation } from "react-i18next"

import { useMutation as useApolloMutation } from "@apollo/client"

import { callMutation } from "shared/api/callMutation"
import { DeleteResponseTypename, MutationTypename } from "shared/api/types"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"

import {
  DeleteFollowUpConfigurationDocument,
  DeleteFollowUpConfigurationMutation,
  DeleteFollowUpConfigurationMutationVariables
} from "../graphql/mutations.generated"
import { FollowUpConfigurationsDocument } from "../graphql/queries.generated"

export function useDeleteFollowUpConfiguration() {
  const { t } = useTranslation()
  const [apolloMutate, rest] = useApolloMutation<
    DeleteFollowUpConfigurationMutation,
    DeleteFollowUpConfigurationMutationVariables
  >(DeleteFollowUpConfigurationDocument)

  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t(
        "Request.FollowUpConfiguration.DELETE_FOLLOW_UP_CONFIGURATION.Start"
      ),
      success: t(
        "Request.FollowUpConfiguration.DELETE_FOLLOW_UP_CONFIGURATION.Success"
      ),
      failure: t(
        "Request.FollowUpConfiguration.DELETE_FOLLOW_UP_CONFIGURATION.Failure"
      )
    }
  })

  const deleteFollowUpConfiguration = useEvent((id: string) => {
    const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast()

    return callMutation(apolloMutate, {
      variables: { id },
      transform: data => data.followUpConfiguration.success,
      apolloOptions: {
        optimisticResponse: {
          __typename: MutationTypename,
          followUpConfiguration: {
            __typename: DeleteResponseTypename,
            success: true
          }
        },
        refetchQueries: [{ query: FollowUpConfigurationsDocument }],
        awaitRefetchQueries: true,
        onCompleted: raiseSuccessToast,
        onError: raiseErrorToast
      }
    })
  })

  return { deleteFollowUpConfiguration, ...rest }
}
